import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "./shared";
import { NEW_LEDGER_OPTION, fundBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import FundForm from "./FundForm";

export default function FundEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fund, setFund] = useState(null);
  const [fundLedgers, setFundLedgers] = useState([]);
  const [form, setForm] = useState(fundBlank);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [fundRes, ledgersRes] = await Promise.all([
          api.get(`/society/accounting/engine/funds/${id}`),
          api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        ]);
        const row = fundRes.data;
        setFund(row);
        setFundLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "fund"));
        setForm({
          fundName: row.fundName || "", fundCode: row.fundCode || "", description: row.description || "",
          linkedLedgerId: row.linkedLedgerId || NEW_LEDGER_OPTION, newLedgerName: "", fundType: row.fundType || "general",
          active: row.active !== false, startDate: row.startDate || "", notes: row.notes || "",
          openingBalance: row.openingBalance || 0, openingBalanceSide: row.openingBalanceSide || "credit",
          openingAsOfDate: row.openingAsOfDate || "",
        });
      } catch (err) {
        toast.error(formatErr(err, "Could not load this fund"));
        navigate(`${ACCOUNTING_BASE}/funds`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const save = async () => {
    if (!form.fundName.trim()) { toast.error("Fund name is required"); return; }
    try {
      setBusy(true);
      let linkedLedgerId = form.linkedLedgerId;
      const openingBalance = form.openingBalance === "" ? 0 : Number(form.openingBalance);
      if (linkedLedgerId === NEW_LEDGER_OPTION) {
        const ledgerName = (form.newLedgerName || form.fundName).trim();
        const ledgerRes = await api.post("/society/accounting/engine/ledgers", {
          name: ledgerName, ledgerGroup: "fund", openingBalance, openingSide: form.openingBalanceSide,
          openingAsOfDate: form.openingAsOfDate,
        });
        linkedLedgerId = ledgerRes.data.id;
      }
      await api.patch(`/society/accounting/engine/funds/${id}`, { ...form, linkedLedgerId, openingBalance });
      toast.success("Fund updated");
      navigate(`${ACCOUNTING_BASE}/funds/${id}`);
    } catch (err) {
      toast.error(formatErr(err, "Could not save fund"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!fund) return null;

  return (
    <div className="space-y-5">
      <div>
        <Link to={`${ACCOUNTING_BASE}/funds/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
          &larr; Back to fund
        </Link>
        <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit {fund.fundName}</h2>
      </div>

      <SectionCard title="Fund details">
        <FundForm
          form={form}
          setForm={setForm}
          availableLedgers={fundLedgers}
          ledgerLocked={!!fund.hasPostedTransactions}
          busy={busy}
          onSubmit={save}
          onCancel={() => navigate(`${ACCOUNTING_BASE}/funds/${id}`)}
          submitLabel="Save changes"
          isEdit
        />
      </SectionCard>
    </div>
  );
}
